import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import supabase from '../config/supabaseClient';
import { colors } from '../styles/colors';
import { useSubscription } from '../hooks/useSubscription';

const SmsPrompt = () => {
  const { user } = useAuth();
  const { isActive: isSubActive } = useSubscription();
  const navigate = useNavigate();

  const smsPromptRefreshGuardRef = useRef({ inFlight: false, lastAt: 0 });
  const smsPromptImpressionRecordedRef = useRef(false);
  const smsPromptDismissedThisSessionRef = useRef(false);
  const previousUserIdRef = useRef(null);

  const [smsPrompt, setSmsPrompt] = useState({
    loading: true,
    show: false,
    dismissing: false,
    dismissedThisSession: false,
  });

  const SMS_PROMPT_MAX_SIGNINS = 3;

  const getLocalSeenCount = useCallback(() => {
    if (!user?.id) return 0;
    try {
      const raw = window.localStorage.getItem(`sms_prompt_seen_count:${user.id}`);
      const n = Number(raw);
      return Number.isFinite(n) ? n : 0;
    } catch {
      return 0;
    }
  }, [user?.id]);

  const setLocalSeenCount = useCallback((n) => {
    if (!user?.id) return;
    try {
      window.localStorage.setItem(`sms_prompt_seen_count:${user.id}`, String(n));
    } catch {
      // ignore
    }
  }, [user?.id]);



  const markPromptSeenThisSession = useCallback(() => {
    if (!user?.id) return;
    try {
      window.sessionStorage.setItem(`sms_prompt_seen_this_session:${user.id}`, '1');
    } catch {
      // ignore
    }
  }, [user?.id]);

  // Check if dismissed this session (persists across page navigations)
  const hasDismissedPromptThisSession = useCallback(() => {
    if (!user?.id) return false;
    try {
      return window.sessionStorage.getItem(`sms_prompt_dismissed_this_session:${user.id}`) === '1';
    } catch {
      return false;
    }
  }, [user?.id]);

  const markPromptDismissedThisSession = useCallback(() => {
    if (!user?.id) return;
    try {
      window.sessionStorage.setItem(`sms_prompt_dismissed_this_session:${user.id}`, '1');
    } catch {
      // ignore
    }
  }, [user?.id]);

  const fetchSmsPromptState = useCallback(async () => {
    if (!user) {
      return;
    }

    // Check if user has an active session (actually signed in)
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setSmsPrompt((p) => ({
        ...p,
        loading: false,
        show: false,
      }));
      return;
    }

    
    const sessionStorageDismissed = hasDismissedPromptThisSession();
    const refDismissed = smsPromptDismissedThisSessionRef.current;
    const sessionStorageSeen = window.sessionStorage.getItem(`sms_prompt_seen_this_session:${user.id}`) === '1';

    if ((sessionStorageDismissed || refDismissed) && sessionStorageSeen) {
      setSmsPrompt((p) => ({
        ...p,
        loading: false,
        show: false,
        dismissing: false,
        dismissedThisSession: true,
      }));
      return;
    }

    
    const dismissedThisSessionBeforeFetch = sessionStorageDismissed || refDismissed;
    if (dismissedThisSessionBeforeFetch) {
      const alreadyShown = window.sessionStorage.getItem(`sms_prompt_seen_this_session:${user.id}`) === '1';

      if (alreadyShown) {
        // User dismissed AND we've shown it - don't fetch, don't show
        setSmsPrompt((p) => ({
          ...p,
          loading: false,
          show: false,
          dismissing: false,
          dismissedThisSession: true,
        }));
        return;
      }
    }

    try {
      // Guard: tab return can trigger multiple refreshes; prevent spamming reads.
      const now = Date.now();
      if (smsPromptRefreshGuardRef.current.inFlight) {
        return;
      }
      if (now - (smsPromptRefreshGuardRef.current.lastAt || 0) < 1500) {
        return;
      }
      smsPromptRefreshGuardRef.current.inFlight = true;

      // If the column isn't migrated yet, Supabase/PostgREST will return 400.
      let data = null;
      let supportsSeenCount = true;

      const first = await supabase
        .from('profiles')
        .select('phone_number, sms_prompt_seen_count, sms_opt_in, sms_prompt_dismissed_at')
        .eq('user_id', user.id)
        .maybeSingle();

      if (first.error) {
        console.error('[SmsPrompt] primary profile fetch error', first.error);
        const msg = (first.error.message || '').toLowerCase();
        if (msg.includes('sms_prompt_seen_count') && msg.includes('does not exist')) {
          supportsSeenCount = false;
          const fallback = await supabase
            .from('profiles')
            .select('phone_number, sms_opt_in, sms_prompt_dismissed_at')
            .eq('user_id', user.id)
            .maybeSingle();
          if (fallback.error) throw fallback.error;
          data = fallback.data;
        } else {
          throw first.error;
        }
      } else {
        data = first.data;
      }

      const phone = data?.phone_number || '';
      const dismissedAt = data?.sms_prompt_dismissed_at;
      const seenCount = supportsSeenCount
        ? (Number.isFinite(Number(data?.sms_prompt_seen_count)) ? Number(data?.sms_prompt_seen_count) : 0)
        : getLocalSeenCount();

      const underLimit = seenCount < SMS_PROMPT_MAX_SIGNINS;
      const dismissedGate = supportsSeenCount ? true : !dismissedAt;

     
      if (!dismissedAt && dismissedThisSessionBeforeFetch) {
        // console.log('SmsPrompt Database dismissal cleared but user dismissed in current session - KEEPING dismissal flag');
        // Don't clear sessionStorage - keep the dismissal flag
      } else if (!dismissedAt && !dismissedThisSessionBeforeFetch) {
        // Database dismissal was cleared AND user hasn't dismissed in current session
        
      }

     
      const alreadyShownThisSession = window.sessionStorage.getItem(`sms_prompt_seen_this_session:${user.id}`) === '1';


      if (alreadyShownThisSession && !phone) {
       
        setSmsPrompt((p) => ({
          ...p,
          loading: false,
          show: false,
          dismissing: false,
        }));
        return;
      }

     
      const currentSessionStorageDismissed = hasDismissedPromptThisSession();
      const currentRefDismissed = smsPromptDismissedThisSessionRef.current;
      const dismissedThisSession = dismissedThisSessionBeforeFetch ||
        currentRefDismissed ||
        currentSessionStorageDismissed;


      if (dismissedThisSession) {
        setSmsPrompt((p) => ({
          ...p,
          loading: false,
          show: false,
          dismissing: false,
          dismissedThisSession: true,
        }));
        return;
      }

      const shouldShow =
        !phone &&
        isSubActive &&
        underLimit &&
        dismissedGate &&
        !smsPromptDismissedThisSessionRef.current &&
        !alreadyShownThisSession;


      const needsToRecordImpression = shouldShow && !smsPromptImpressionRecordedRef.current && !alreadyShownThisSession;

      if (needsToRecordImpression) {
        smsPromptImpressionRecordedRef.current = true;
        markPromptSeenThisSession();
      }

      setSmsPrompt((p) => ({
        ...p,
        loading: false,
        show: shouldShow,
        dismissing: false,
      }));

     
      if (needsToRecordImpression) {
      
        try {
          const nextSeenCount = Math.min(SMS_PROMPT_MAX_SIGNINS, seenCount + 1);

          if (supportsSeenCount) {
            const { error: upsertError } = await supabase
              .from('profiles')
              .upsert(
                {
                  user_id: user.id,
                  updated_at: new Date(),
                  sms_prompt_seen_count: nextSeenCount,
                  sms_prompt_last_shown_at: new Date(),
                },
                { onConflict: 'user_id' }
              );

            if (upsertError) throw upsertError;
          } else {
            setLocalSeenCount(nextSeenCount);
          }
        } catch (impressionErr) {
          console.warn('Failed to record SMS prompt impression:', impressionErr);
        }
      } else if (alreadyShownThisSession && shouldShow) {
        setSmsPrompt((p) => ({
          ...p,
          loading: false,
          show: false,
          dismissing: false,
        }));
        return;
      }
    } catch (e) {
      console.error('Failed to load SMS prompt state:', e);
      // Fail closed (don't annoy user)
      setSmsPrompt((p) => ({ ...p, loading: false, show: false, dismissing: false }));
    }
    finally {
      smsPromptRefreshGuardRef.current.inFlight = false;
      smsPromptRefreshGuardRef.current.lastAt = Date.now();
    }
  }, [user, isSubActive, getLocalSeenCount, setLocalSeenCount, markPromptSeenThisSession, hasDismissedPromptThisSession]);

  // When the logged-in user changes, treat it as a fresh "sign-in" session for banner counting.
  useEffect(() => {
    const currentUserId = user?.id;
    const previousUserId = previousUserIdRef.current;

    const userChanged = previousUserId !== null && previousUserId !== currentUserId;

  

    if (userChanged) {
      smsPromptImpressionRecordedRef.current = false;
      smsPromptDismissedThisSessionRef.current = false;

      // Clear sessionStorage flags for new user
      if (previousUserId) {
        try {
          window.sessionStorage.removeItem(`sms_prompt_seen_this_session:${previousUserId}`);
          window.sessionStorage.removeItem(`sms_prompt_dismissed_this_session:${previousUserId}`);
        } catch {
          // ignore
        }
      }
      setSmsPrompt((p) => ({ ...p, dismissedThisSession: false }));
    }

    previousUserIdRef.current = currentUserId;
  }, [user?.id]);

  useEffect(() => {
    if (user) {
      // Check if user has an active session (actually signed in)
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (!session) {
          setSmsPrompt((p) => ({
            ...p,
            loading: false,
            show: false,
          }));
          return;
        }


        const isDismissed = hasDismissedPromptThisSession();
        const isSeen = window.sessionStorage.getItem(`sms_prompt_seen_this_session:${user.id}`) === '1';


        if (isDismissed && isSeen) {

          setSmsPrompt((p) => ({
            ...p,
            loading: false,
            show: false,
            dismissing: false,
            dismissedThisSession: true,
          }));
          return;
        }

        fetchSmsPromptState();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user) return;

    const onVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      const isDismissed = hasDismissedPromptThisSession();
      const alreadyShown = window.sessionStorage.getItem(`sms_prompt_seen_this_session:${user.id}`) === '1';


      if (isVisible && !isDismissed && !alreadyShown) {
        fetchSmsPromptState();
      } else {
        // console.log('[SmsPrompt] ⏭️ Visibility change - skipping refresh (dismissed, already shown, or not visible)');
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const dismissSmsPrompt = async () => {
    if (!user) return;


    try {
      smsPromptDismissedThisSessionRef.current = true;
      markPromptDismissedThisSession();
      smsPromptImpressionRecordedRef.current = true;
      markPromptSeenThisSession();

      setSmsPrompt((p) => ({ ...p, dismissing: true }));

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ sms_prompt_dismissed_at: new Date() })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      setSmsPrompt({ loading: false, show: false, dismissing: false, dismissedThisSession: true });
    } catch (e) {
      console.error('Failed to dismiss SMS prompt:', e);
      setSmsPrompt((p) => ({ ...p, dismissing: false }));
    }
  };

  // Dismiss popup for this session when user clicks "Add Phone Number" link
  const handleAddPhoneClick = (e) => {
    e.preventDefault();

    smsPromptDismissedThisSessionRef.current = true;
    markPromptDismissedThisSession();
    smsPromptImpressionRecordedRef.current = true;
    markPromptSeenThisSession();


    setSmsPrompt({ loading: false, show: false, dismissing: false, dismissedThisSession: true });
    navigate('/profile');
  };

  // Don't render anything if user is not authenticated
  if (!user) return null;

  if (smsPrompt.loading || !smsPrompt.show) return null;

  return (
    <div
      role="dialog"
      aria-label="Enable SMS reminders"
      aria-live="polite"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.18)',
        }}
      />

      <div
        style={{
          position: 'absolute',
          right: '16px',
          bottom: '16px',
          left: '16px',
          display: 'flex',
          justifyContent: 'flex-end',
          pointerEvents: 'none',
        }}
      >
        <div
          style={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: '520px',
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            borderLeft: `6px solid ${colors.primary}`,
            borderRadius: '14px',
            padding: '14px 16px 14px 16px',
            boxShadow: '0 18px 45px rgba(0,0,0,0.22)',
            transform: 'translateY(0)',
          }}
        >
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '800', color: colors.text.primary, marginBottom: '6px' }}>
                📱 Get race day reminders via text
              </div>

              <div style={{ color: colors.text.secondary, fontSize: '0.95rem', lineHeight: 1.4 }}>
                Add your phone number to receive summaries and timing alerts.
              </div>
            </div>
          </div>

          <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <Link
              to="/profile"
              onClick={handleAddPhoneClick}
              style={{
                backgroundColor: colors.primary,
                color: 'white',
                padding: '10px 12px',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: '700',
                fontSize: '0.95rem',
              }}
            >
              Add Phone Number on Profile Page
            </Link>

            <button
              type="button"
              onClick={dismissSmsPrompt}
              disabled={smsPrompt.dismissing}
              style={{
                backgroundColor: 'transparent',
                color: colors.text.primary,
                padding: '10px 12px',
                borderRadius: '10px',
                border: `1px solid ${colors.border}`,
                cursor: smsPrompt.dismissing ? 'not-allowed' : 'pointer',
                fontWeight: '700',
                fontSize: '0.95rem',
              }}
            >
              {smsPrompt.dismissing ? 'Dismissing…' : 'Dismiss'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsPrompt;
